import { useEffect, useRef } from "react";
import type { SlidePlaylistItem } from "@wedding/shared";
import { cdnUrl } from "../lib/cdn";

/**
 * Preloads the upcoming slide announced by the sync server (`slideState.next`).
 *
 * Two jobs in one:
 *  - warms the CDN edge cache during the PREVIOUS slide, so 100 projector +
 *    phone viewers don't stampede the origin at cut time;
 *  - crowd-sources video durations: probing `loadedmetadata` yields the real
 *    length, which the page reports to the SlideshowRoom DO (duration_report)
 *    so the shared timeline can end the video slide exactly on time.
 */
export function useSlidePreload(
  next: SlidePlaylistItem | null,
  onVideoDuration: (mediaId: string, durationMs: number) => void,
) {
  const probedRef = useRef<Set<string>>(new Set());

  const nextId = next?.id;
  const nextKind = next?.kind;
  const nextUrl = next?.url;

  useEffect(() => {
    if (!nextId || !nextUrl || probedRef.current.has(nextId)) return;
    probedRef.current.add(nextId);

    if (nextKind === "image") {
      const img = new Image();
      img.src = cdnUrl(nextUrl, { w: 2048 });
      return;
    }

    // Detached element: metadata-only fetch, never attached to the DOM.
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) {
        onVideoDuration(nextId, Math.round(v.duration * 1000));
      }
      v.removeAttribute("src");
      v.load();
    };
    v.onerror = () => {
      v.removeAttribute("src");
    };
    v.src = nextUrl;
    // No cleanup on next-change: let an in-flight probe finish (it's tiny and
    // its duration report is still valuable when the slide comes around).
  }, [nextId, nextKind, nextUrl, onVideoDuration]);
}
